"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, type ComponentProps } from "react";
import { Icon } from "@/components/icons/interface";
type Port = readonly [string, string];
export type Capability = {
  name: string;
  source: string;
  icon: ComponentProps<typeof Icon>["name"];
  inputs: readonly Port[];
  outputs: readonly Port[];
  flow?: boolean;
};
export const capabilities = {
  multiply: {
    name: "Multiply",
    source: "Core",
    icon: "close",
    inputs: [["a", "Number"], ["b", "Number"]],
    outputs: [["value", "Number"]]
  },
  add: {
    name: "Add",
    source: "Core",
    icon: "plus",
    inputs: [["a", "Number"], ["b", "Number"]],
    outputs: [["value", "Number"]]
  },
  open: {
    name: "Open file",
    source: "Files",
    icon: "folder",
    inputs: [["Path", "Buffer"]],
    outputs: [["File", "File"]],
    flow: true
  },
  read: {
    name: "Read bytes",
    source: "Files",
    icon: "folder",
    inputs: [["File", "File"], ["Buffer", "Buffer"], ["Bytes", "u64"]],
    outputs: [["Read", "u64"]],
    flow: true
  },
  write: {
    name: "Write bytes",
    source: "Files",
    icon: "folder",
    inputs: [["File", "File"], ["Buffer", "Buffer"], ["Bytes", "u64"]],
    outputs: [["Written", "u64"]],
    flow: true
  },
  renderer: {
    name: "Create renderer",
    source: "GPU",
    icon: "monitor",
    inputs: [["Title", "Buffer"], ["Width", "i32"], ["Height", "i32"]],
    outputs: [["Renderer", "Renderer"]],
    flow: true
  },
  cube: {
    name: "Create cube",
    source: "GPU",
    icon: "layers",
    inputs: [["Renderer", "Renderer"]],
    outputs: [["Cube", "Cube"]],
    flow: true
  },
  draw: {
    name: "Draw cube",
    source: "GPU",
    icon: "image",
    inputs: [["Renderer", "Renderer"], ["Cube", "Cube"], ["Frame", "i32"]],
    outputs: [],
    flow: true
  }
} as const satisfies Record<string, Capability>;
export type CapabilityId = keyof typeof capabilities;
export const packageNames = ["Core", "Files", "GPU"] as const;
export type PackageName = typeof packageNames[number];
function NodeFace(t0) {
  const $ = _c(16);
  const {
    node
  } = t0;
  let t1;
  if ($[0] !== node.icon) {
    t1 = <Icon name={node.icon} />;
    $[0] = node.icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== node.name) {
    t2 = <h3>{node.name}</h3>;
    $[2] = node.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== node.source) {
    t3 = <span>{node.source}</span>;
    $[4] = node.source;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <header>{t1}{t2}{t3}</header>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["inputs", "outputs"];
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== node) {
    t6 = <div className="capability-ports">{(t5 as const).map(side => <div key={side} className={`capability-${side}`} aria-label={side}>{node.flow && <div className="capability-flow"><i aria-hidden="true" /><span>{side === "inputs" ? "In" : "Next"}</span></div>}{node[side].map(_temp)}</div>)}</div>;
    $[11] = node;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function _temp(t0) {
  const [name, type] = t0;
  return <div key={name}><i aria-hidden="true" /><span>{name}<code>{type}</code></span></div>;
}
export function CapabilityWorkbench(t0) {
  const $ = _c(25);
  const {
    source,
    initial: t1
  } = t0;
  const initial = t1 === undefined ? "multiply" : t1;
  const [selected, setSelected] = useState(initial);
  const [query, setQuery] = useState("");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Object.keys(capabilities);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== query || $[2] !== source) {
    t3 = (t2 as CapabilityId[]).filter(id => (!source || capabilities[id].source === source) && `${capabilities[id].name} ${capabilities[id].source}`.toLowerCase().includes(query.trim().toLowerCase()));
    $[1] = query;
    $[2] = source;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const entries = t3;
  const visible = entries.includes(selected) ? selected : entries[0];
  const node = visible ? capabilities[visible] : undefined;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Icon name="search" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => setQuery(event.target.value);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== query) {
    t6 = <label className="capability-search">{t4}<input type="search" aria-label="Find a node" placeholder={"Find a node\u2026"} value={query} onChange={t5} /></label>;
    $[6] = query;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon name="package" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== source) {
    t8 = <span className="capability-source">{t7}{source}</span>;
    $[9] = source;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <div className="capability-canvas-top">{t6}{t8}</div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== node || $[15] !== visible) {
    t10 = <div className="capability-canvas"><div className="capability-object">{node ? <article className="capability-node" key={visible} aria-label={`${node.name} node`}><NodeFace node={node} /></article> : <div className="capability-empty"><p>No matching nodes</p><button onClick={() => setQuery("")}>Clear search</button></div>}</div></div>;
    $[14] = node;
    $[15] = visible;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== entries || $[18] !== visible) {
    t11 = <div className="capability-results" aria-label="Available node previews">{entries.map(id_0 => <button key={id_0} aria-pressed={visible === id_0} onClick={() => setSelected(id_0)}><Icon name={capabilities[id_0].icon} /><span>{capabilities[id_0].name}</span></button>)}</div>;
    $[17] = entries;
    $[18] = visible;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <footer><span>Public interface</span><span>Read-only preview</span></footer>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t9) {
    t13 = <div className="capability-workbench">{t9}{t10}{t11}{t12}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
}